import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography, Box, Alert, Menu, MenuItem, Button } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import FormSection from "./FormSection";
import ResultsSection from "./ResultsSection";
import { TWButton } from "../../../shared/components";
import { PageLayout } from "../../../shared/components";
import { useMe } from "../../../shared/hooks/useMe";
import { canWriteQuestionnaireFlow } from "../../../shared/permissions/capabilities";
import { useStreamingAnswers, Answer } from "../hooks/useStreamingAnswers";
import {
  getQuestionnaireExportFileUrl,
  translateAnswerText,
} from "../utils/questionnaireApi";
import { isEnglishQuestionLocale } from "../utils/locale";
import { apiFetch, getApiAuthHeaders } from "../../../shared/utils/api";
import { getAccessToken } from "../../../shared/auth/okta";
import {
  cleanQuestionText,
  csvQuestionNumberCell,
  escapeCSVValue,
} from "../utils/qaHelpers";
import { FormData, LocationState } from "../types/forms";
import { BackendResponse } from "../types/responses";

const SecurityQuestionnaire: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const { me } = useMe();
  const canWriteQuest = canWriteQuestionnaireFlow(me);

  // Use the streaming answers hook
  const {
    status,
    statusMessage,
    progress,
    answers,
    error: streamError,
    sessionUuid,
    batchErrors,
    submitQuestions,
    resetState,
    isLoading,
    hasAnswers,
  } = useStreamingAnswers();

  const [formData, setFormData] = useState<FormData>({
    clientName: "",
    engagementType: [],
    laptopType: "",
    countryOffice: "",
    market: "",
    contractType: "",
    regulatoryConstraints: "",
    securityQuestions: "",
  });

  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  // Track edited answers by answer id
  const [editedAnswers, setEditedAnswers] = useState<Record<string, string>>(
    {},
  );
  // Local error state for form validation
  const [formError, setFormError] = useState<string | null>(null);

  // Computed results for backward compatibility with ResultsSection
  const results: BackendResponse | null = hasAnswers
    ? { answers, session_uuid: sessionUuid || undefined }
    : null;

  // Store file_uuid from location state
  const fileUuid = locationState?.file_uuid;
  // Store structured questions from location state (for file upload flow)
  const structuredQuestions = locationState?.structuredQuestions;

  // Pre-fill security questions from navigation state
  useEffect(() => {
    if (locationState?.structuredQuestions) {
      // File upload flow: convert structured questions to display text
      const questionsText = locationState.structuredQuestions
        .map((q) => `${q.question_number}. ${q.question_text}`)
        .join("\n");
      setFormData((prev) => ({
        ...prev,
        securityQuestions: questionsText,
      }));
    } else if (locationState?.prefillQuestions) {
      // Legacy: plain text questions
      setFormData((prev) => ({
        ...prev,
        securityQuestions: locationState.prefillQuestions || "",
      }));
    }
  }, [locationState?.prefillQuestions, locationState?.structuredQuestions]);

  // Handler for when an answer is edited in ResultsSection
  const handleAnswerEdit = (answerId: string, newAnswer: string) => {
    setEditedAnswers((prev) => ({
      ...prev,
      [answerId]: newAnswer,
    }));
  };

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleCheckboxChange =
    (value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        engagementType: event.target.checked
          ? [...prev.engagementType, value]
          : prev.engagementType.filter((item) => item !== value),
      }));
    };

  const handleProcessQuestions = async () => {
    if (!formData.clientName.trim()) {
      setFormError("Client name is required");
      return;
    }

    if (!formData.securityQuestions.trim()) {
      setFormError("Security questions are required");
      return;
    }

    setFormError(null);

    // Build metadata object
    const metadata = {
      client_name: formData.clientName || null,
      engagement_type:
        formData.engagementType.length > 0
          ? formData.engagementType.join(", ")
          : null,
      laptop_type: formData.laptopType || null,
      country_office: formData.countryOffice || null,
      market: formData.market || null,
      contract_type: formData.contractType || null,
      regulatory_constraints: formData.regulatoryConstraints || null,
      file_uuid: fileUuid || null,
    };

    // Use the streaming hook to submit questions
    // Pass structured questions if from file upload (preserves sheet_name/row_index), otherwise plain text
    await submitQuestions(
      structuredQuestions ? null : formData.securityQuestions,
      metadata,
      structuredQuestions,
    );
  };

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      securityQuestions: "",
    }));
    setFormError(null);
    setEditedAnswers({});
    resetState();
    // Clear location state so next "Get answer" uses plain text from form, not stale file-upload context
    navigate("/questionnaire/questions", { replace: true });
  };

  // Build query parameters for backend download API
  const buildDownloadParams = (
    fileUuid: string,
    sessionUuid?: string,
    translateAnswers?: boolean,
  ): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("file_uuid", fileUuid);

    if (sessionUuid) {
      params.append("session_uuid", sessionUuid);
    }
    if (translateAnswers) {
      params.append("translate_answers", "true");
    }

    return params;
  };

  // Extract filename from Content-Disposition (axios headers object or fetch Headers)
  const extractFileNameFromDisposition = (
    contentDisposition: string | null | undefined,
    defaultFileName: string = "questionnaire_with_answers.xlsx",
  ): string => {
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(
        /filename="?([^";\n]+)"?/i,
      );
      if (fileNameMatch) {
        return fileNameMatch[1].trim();
      }
    }
    return defaultFileName;
  };

  // Generic function to trigger file download from blob
  const triggerFileDownload = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download file from backend API (fetch + apiFetch so Authorization is always sent; avoids axios/blob edge cases)
  const downloadFileFromBackend = async (
    fileUuid: string,
    sessionUuid?: string,
    translateAnswers?: boolean,
  ): Promise<void> => {
    const params = buildDownloadParams(fileUuid, sessionUuid, translateAnswers);
    const accessToken = getAccessToken();
    const url = getQuestionnaireExportFileUrl(params);

    const response = await apiFetch(url, {
      method: "GET",
      headers: getApiAuthHeaders(accessToken),
    });

    if (!response.ok) {
      const errBody = await response.text();
      let detail = errBody;
      try {
        const parsed = JSON.parse(errBody) as { detail?: unknown };
        if (parsed.detail != null) {
          detail =
            typeof parsed.detail === "string"
              ? parsed.detail
              : JSON.stringify(parsed.detail);
        }
      } catch {
        /* use raw text */
      }
      throw new Error(detail || `Download failed (${response.status})`);
    }

    const blob = await response.blob();
    const fileName = extractFileNameFromDisposition(
      response.headers.get("content-disposition"),
    );

    triggerFileDownload(blob, fileName);
  };

  // Get the latest answer for a question (prioritizes local edits > saved edits > original)
  const getLatestAnswer = (answer: Answer): string => {
    return editedAnswers[answer.id] || answer.user_edited_ans || answer.answer;
  };

  // Generate CSV filename with client name and date
  const generateCSVFilename = (clientName: string): string => {
    const currentDate = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    const sanitizedClientName =
      clientName.trim().replace(/[^a-zA-Z0-9]/g, "_") || "client";
    return `${sanitizedClientName}_${currentDate}.csv`;
  };

  // Download CSV file (client-side generation)
  const downloadCSVFile = (
    answers: Answer[],
    translatedResponses?: string[],
  ): void => {
    const csvHeader = "Question Number,Question,Response\n";
    const csvRows = answers
      .map((answer, idx) => {
        const qNum = escapeCSVValue(csvQuestionNumberCell(answer, idx));
        const question = escapeCSVValue(cleanQuestionText(answer.question));
        const latest =
          translatedResponses && translatedResponses[idx] !== undefined
            ? translatedResponses[idx]
            : getLatestAnswer(answer);
        const response = escapeCSVValue(latest);
        return `${qNum},${question},${response}`;
      })
      .join("\n");
    const csvContent = csvHeader + csvRows;
    const fileName = generateCSVFilename(
      formData.clientName + (translatedResponses ? "_translated" : ""),
    );

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    triggerFileDownload(blob, fileName);
  };

  // Handle download error
  const handleDownloadError = (error: any): void => {
    console.error("Error downloading file:", error);
    const errorMessage =
      error.response?.data?.detail || error.message || "Unknown error";
    alert(`Failed to download file: ${errorMessage}`);
  };

  // Open download menu
  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  // Close download menu
  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  // Download CSV file handler
  const handleDownloadCSV = async () => {
    handleDownloadMenuClose();
    if (!results || !results.answers || results.answers.length === 0) {
      return;
    }
    setDownloading(true);

    try {
      downloadCSVFile(results.answers);
    } catch (error: any) {
      handleDownloadError(error);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  // Download original file with answers handler
  const handleDownloadOriginalFile = async () => {
    handleDownloadMenuClose();
    if (!results || !results.answers || results.answers.length === 0) {
      return;
    }
    if (!fileUuid) {
      alert("Original file not available. Please use CSV download instead.");
      return;
    }
    setDownloading(true);

    try {
      await downloadFileFromBackend(fileUuid, sessionUuid || undefined, false);
    } catch (error: any) {
      handleDownloadError(error);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  const handleDownloadCSVTranslated = async () => {
    handleDownloadMenuClose();
    if (!results?.answers?.length) {
      return;
    }
    setDownloading(true);
    try {
      const responses: string[] = [];
      for (const a of results.answers) {
        const base = getLatestAnswer(a);
        if (!a.question_locale || isEnglishQuestionLocale(a.question_locale)) {
          responses.push(base);
        } else {
          const r = await translateAnswerText({
            text: base,
            target_locale: a.question_locale,
            qaf_id: a.qaf_id,
          });
          responses.push(r.translated_text);
        }
      }
      downloadCSVFile(results.answers, responses);
    } catch (error: unknown) {
      handleDownloadError(error);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  const handleDownloadOriginalFileTranslated = async () => {
    handleDownloadMenuClose();
    if (!results?.answers?.length) {
      return;
    }
    if (!fileUuid) {
      alert("Original file not available. Please use CSV download instead.");
      return;
    }
    setDownloading(true);
    try {
      await downloadFileFromBackend(fileUuid, sessionUuid || undefined, true);
    } catch (error: unknown) {
      handleDownloadError(error);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  // Custom right actions for the header
  const headerRightActions = results ? (
    <>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={handleReset}
        sx={{
          color: "#333",
          textTransform: "none",
          fontSize: "0.9rem",
          fontWeight: 500,
          "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
        }}
      >
        Ask questions
      </Button>
      <TWButton
        startIcon={<DownloadIcon />}
        onClick={handleDownloadMenuOpen}
        loading={downloading}
        disabled={downloading}
        sx={{ fontSize: "1rem", minWidth: 170 }}
        label="Download all"
      />
    </>
  ) : undefined;

  return (
    <PageLayout rightActions={headerRightActions}>
      {/* Download Options Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          onClick={handleDownloadCSV}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": { backgroundColor: "rgba(72, 161, 173, 0.08)" },
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 500, color: "#333", fontSize: "0.95rem" }}
            >
              Download as CSV
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => void handleDownloadCSVTranslated()}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": { backgroundColor: "rgba(72, 161, 173, 0.08)" },
          }}
        >
          <Typography
            sx={{ fontWeight: 500, color: "#333", fontSize: "0.95rem" }}
          >
            Download as CSV (original language)
          </Typography>
        </MenuItem>
        {fileUuid && (
          <MenuItem
            onClick={handleDownloadOriginalFile}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": { backgroundColor: "rgba(72, 161, 173, 0.08)" },
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 500, color: "#333", fontSize: "0.95rem" }}
              >
                Download original file
              </Typography>
            </Box>
          </MenuItem>
        )}
        {fileUuid && (
          <MenuItem
            onClick={() => void handleDownloadOriginalFileTranslated()}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": { backgroundColor: "rgba(72, 161, 173, 0.08)" },
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 500, color: "#333", fontSize: "0.95rem" }}
              >
                Download original file (original language)
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* Error Alert */}
      {(formError || streamError) && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
          onClose={() => setFormError(null)}
        >
          {formError || streamError}
        </Alert>
      )}

      {/* Batch Errors Warning */}
      {batchErrors.length > 0 && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {batchErrors.length} batch(es) encountered errors during processing.
          Some questions may not have been answered.
        </Alert>
      )}

      {/* Show skeleton loading when processing */}
      {isLoading && !hasAnswers && (
        <ResultsSection
          isProcessing={isLoading}
          results={null}
          onAnswerEdit={handleAnswerEdit}
          progress={progress}
          status={status}
          statusMessage={statusMessage}
        />
      )}

      {/* Form Section - Hide when processing or results are available */}
      {!isLoading && !hasAnswers && (
        <Box>
          {/* Title and Instructions */}
          <Box sx={{ mb: 4, textAlign: "left" }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: "2rem",
                color: "#333",
                mb: 2,
              }}
            >
              Ask questions
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#666",
                fontSize: "1rem",
                mb: 1,
              }}
            >
              Enter the opportunity information below and then copy the client's
              security questions.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#999",
                fontSize: "0.875rem",
              }}
            >
              * indicates a required field
            </Typography>
          </Box>

          {/* Form Sections (Client Opportunity + Security Questions) */}
          <FormSection
            formData={formData}
            isProcessing={isLoading}
            onInputChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
            onProcessQuestions={handleProcessQuestions}
            onReset={handleReset}
            isQuestionsReadOnly={!!structuredQuestions}
            disableSubmit={!canWriteQuest}
          />
        </Box>
      )}

      {/* Show results when available (including during streaming) */}
      {hasAnswers && (
        <ResultsSection
          isProcessing={isLoading}
          results={results}
          onAnswerEdit={handleAnswerEdit}
          progress={progress}
          status={status}
          statusMessage={statusMessage}
        />
      )}
    </PageLayout>
  );
};

export default SecurityQuestionnaire;
